import { useState } from 'react'
import { isSameDay } from 'date-fns'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { format } from '@/utils/date-fns'

export interface WorkPeriodAllocationItem {
  id: string
  name: string
  externalId?: string | null
  /** `HH:mm`, as the backend's TimeDelta serialises it. */
  duration: string
}

interface Props {
  title: string
  items: WorkPeriodAllocationItem[]
  countLabel: (amount: number) => string
  datetimeStart: Date
  datetimeEnd: Date
  /** Lets the trigger fill a card instead of sitting inside a table cell. */
  fullWidth?: boolean
}

export const allocationLabel = (item: WorkPeriodAllocationItem) =>
  item.externalId ? `(${item.externalId}) ${item.name}` : item.name

/**
 * The departments or facilities a work day's hours landed on. A single one is
 * shown by name; several collapse to a count that opens the breakdown.
 */
export const WorkPeriodAllocation: React.FC<Props> = ({
  title,
  items,
  countLabel,
  datetimeStart,
  datetimeEnd,
  fullWidth,
}) => {
  const [open, setOpen] = useState(false)

  const [first] = items
  if (!first) {
    return <span className="px-1.5 text-sm text-stone-400">–</span>
  }

  if (items.length === 1) {
    return (
      <span className="block truncate px-1.5 text-sm text-stone-700">
        {allocationLabel(first)}
      </span>
    )
  }

  const dateLabel = isSameDay(datetimeStart, datetimeEnd)
    ? format(datetimeStart, 'EEE dd. MMM')
    : `${format(datetimeStart, 'dd. MMM')} – ${format(datetimeEnd, 'dd. MMM')}`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={event => event.stopPropagation()}
          className={cn(
            'flex min-h-8 items-center rounded-lg px-1.5 py-1 text-left',
            'cursor-pointer hover:bg-stone-100',
            fullWidth ? 'w-full' : 'max-w-full'
          )}
        >
          <span className="truncate rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-700">
            {countLabel(items.length)}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-80 p-0"
        onClick={event => event.stopPropagation()}
      >
        <header className="border-b border-stone-200 px-4 py-3">
          <div className="text-xs font-semibold tracking-[0.14em] uppercase text-stone-500">
            {title}
          </div>
          <div className="text-sm font-medium text-stone-900">{dateLabel}</div>
        </header>
        <ul className="max-h-72 divide-y divide-stone-200 overflow-y-auto">
          {items.map(item => (
            <li
              key={item.id}
              className="flex items-baseline justify-between gap-3 px-4 py-2.5"
            >
              <span className="min-w-0 text-sm break-words text-stone-900">
                {allocationLabel(item)}
              </span>
              <span className="shrink-0 font-mono text-sm text-stone-600">
                {item.duration}
              </span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
